import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiCheckCircle,
  FiClock,
  FiEye,
  FiTarget,
  FiTrendingUp,
} from 'react-icons/fi';
import {
  HomeKpisData,
  HomeKpisFacilityComparisonItem,
} from '../../../interfaces/homeKpis';
import { NoDataPlaceholder } from '../../../ComponnentsUI/common/NoDataPlaceholder';

export type HomeKpiTab =
  | 'map'
  | 'activity'
  | 'clinical'
  | 'training-quality'
  | 'ai-views'
  | 'population'
  | 'facilities-recent';

export const HomeKpiTabs: React.FC<{
  active: HomeKpiTab;
  onChange: (tab: HomeKpiTab) => void;
  labels: Record<HomeKpiTab, string>;
  showFacilitiesRecent: boolean;
  showMap: boolean;
}> = ({ active, onChange, labels, showFacilitiesRecent, showMap }) => {
  const tabs: HomeKpiTab[] = [
    'activity',
    'clinical',
    'ai-views',
    'population',
  ];
  if (showMap) {
    tabs.splice(2, 0, 'training-quality');
  }
  if (showFacilitiesRecent) {
    tabs.splice(1, 0, 'facilities-recent');
  }
  if (showMap) {
    tabs.unshift('map');
  }

  return (
    <div className="flex w-full overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-card-color-100 dark:bg-gray-800 p-1">
      {tabs.map((tab) => {
        const selected = active === tab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => onChange(tab)}
            className={`min-w-fit flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              selected
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:bg-card-color-200 dark:hover:bg-gray-700'
            }`}
          >
            {labels[tab]}
          </button>
        );
      })}
    </div>
  );
};

export const ScopePill: React.FC<{ data: HomeKpisData }> = ({ data }) => {
  const { t } = useTranslation('Dashboard');
  const label =
    data.scope.kind === 'facility'
      ? data.scope.facility_name || data.scope.facility_id
      : t('kpis.scope.allAccessible');

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
      <span className="h-2 w-2 rounded-full bg-blue-500" />
      <span className="max-w-[220px] truncate">{label}</span>
    </div>
  );
};

export const BiometryAverages: React.FC<{
  values: HomeKpisData['clinical']['biometry_averages_cm'];
}> = ({ values }) => {
  const { t } = useTranslation('Dashboard');
  const items = [
    { label: 'BPD', value: values.biparietal_diameter_bpd, unit: 'cm' },
    { label: 'HC', value: values.head_circumference_hc, unit: 'cm' },
    { label: 'AC', value: values.abdominal_circumference_ac, unit: 'cm' },
    { label: 'FL', value: values.femur_length_fl, unit: 'cm' },
    { label: 'EFW', value: values.est_fetal_weight_grams, unit: 'g' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-xl border border-gray-100 dark:border-gray-700 bg-card-color-200 dark:bg-gray-900/40 p-4"
        >
          <div
            className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            title={t(`kpis.biometry.${item.label}`, item.label)}
          >
            {item.label}
          </div>
          <div className="mt-2 text-2xl font-bold text-title-page-color-100 dark:text-white">
            {item.value == null ? '—' : item.value.toLocaleString()}
            {item.value != null && (
              <span className="ml-1 text-xs font-semibold text-gray-400">
                {item.unit}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const GaAgreementSummary: React.FC<{
  agreement: HomeKpisData['ai']['ga_agreement'];
}> = ({ agreement }) => {
  const { t } = useTranslation('Dashboard');
  const total = agreement.total_compared || 0;
  const within2 = total
    ? Math.round((agreement.within_2_weeks / total) * 100)
    : 0;
  const within5 = total
    ? Math.round((agreement.within_5_weeks / total) * 100)
    : 0;
  const items = [
    {
      icon: <FiTarget />,
      label: t('kpis.ga.comparedExams'),
      value: total.toLocaleString(),
      accent: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: <FiCheckCircle />,
      label: t('kpis.ga.withinTwoWeeks'),
      value: `${within2}%`,
      accent: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      icon: <FiClock />,
      label: t('kpis.ga.meanAbsError'),
      value:
        agreement.mean_absolute_error_weeks == null
          ? '—'
          : `${agreement.mean_absolute_error_weeks}w`,
      accent: 'text-amber-600 dark:text-amber-400',
    },
    {
      icon: <FiClock />,
      label: t('kpis.ga.medianAbsError'),
      value:
        agreement.median_absolute_error_weeks == null
          ? '—'
          : `${agreement.median_absolute_error_weeks}w`,
      accent: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-xl border border-gray-100 dark:border-gray-700 bg-card-color-200 dark:bg-gray-900/40 p-4"
        >
          <div
            className={`mb-3 inline-flex rounded-lg bg-white dark:bg-gray-800 p-2 text-lg ${item.accent}`}
          >
            {item.icon}
          </div>
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {item.label}
          </div>
          <div className="mt-1 text-2xl font-bold text-title-page-color-100 dark:text-white">
            {item.value}
          </div>
        </div>
      ))}
      <div className="sm:col-span-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs font-medium text-emerald-700 dark:text-emerald-300">
        {total
          ? t('kpis.ga.withinFiveSummary', { percent: within5 })
          : t('kpis.ga.noPairs')}
      </div>
    </div>
  );
};

export type GrowthWindowKey =
  | 'growthOverBaseline'
  | 'rolling30'
  | 'rolling7'
  | 'rolling90';

const WINDOW_KEYS: GrowthWindowKey[] = [
  'growthOverBaseline',
  'rolling30',
  'rolling7',
  'rolling90',
];

const formatChange = (value: number | null) => {
  if (value == null) {
    return '—';
  }
  return `${value >= 0 ? '+' : ''}${value}%`;
};

export const GrowthSummary: React.FC<{
  growth: HomeKpisData['activity']['growth'];
  windowKey: GrowthWindowKey;
  onWindowChange: (next: GrowthWindowKey) => void;
}> = ({ growth, windowKey, onWindowChange }) => {
  const { t } = useTranslation('Dashboard');
  const window = growth.windows?.[windowKey];

  const currentExams = window?.exams_current ?? null;
  const currentPatients = window?.patients_current ?? null;
  const priorExams = window?.exams_prior ?? null;
  const priorPatients = window?.patients_prior ?? null;
  const examChange = window?.exam_change_percent ?? null;
  const patientChange = window?.patient_change_percent ?? null;

  const items = [
    {
      label: t('kpis.growth.examsCurrent'),
      value: currentExams != null ? currentExams.toLocaleString() : '—',
      meta: formatChange(examChange),
    },
    {
      label: t('kpis.growth.patientsCurrent'),
      value:
        currentPatients != null ? currentPatients.toLocaleString() : '—',
      meta: formatChange(patientChange),
    },
    {
      label: t('kpis.growth.examsPrior'),
      value: priorExams != null ? priorExams.toLocaleString() : '—',
      meta: t('kpis.growth.monthsActive') + ': ' + growth.months_active,
    },
    {
      label: t('kpis.growth.bestMonth'),
      value: growth.best_month || '—',
      meta: growth.best_month_exams
        ? `${growth.best_month_exams.toLocaleString()} ${t('analytics.exams')}`
        : '—',
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">
          {t('kpis.growth.window')}
        </label>
        <select
          value={windowKey}
          onChange={(e) => onWindowChange(e.target.value as GrowthWindowKey)}
          className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs font-semibold text-title-page-color-100 dark:text-white"
        >
          {WINDOW_KEYS.map((k) => (
            <option key={k} value={k}>
              {t(`kpis.growth.windows.${k}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <div
            key={item.label}
            className="rounded-xl border border-gray-100 dark:border-gray-700 bg-card-color-200 dark:bg-gray-900/40 p-4"
          >
            <div className="mb-3 inline-flex rounded-lg bg-white dark:bg-gray-800 p-2 text-lg text-blue-600 dark:text-blue-400">
              <FiTrendingUp />
            </div>
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              {item.label}
            </div>
            <div className="mt-1 text-2xl font-bold text-title-page-color-100 dark:text-white">
              {item.value}
            </div>
            <div className="mt-1 text-xs font-semibold text-gray-400">
              {item.meta}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ViewQuickStats: React.FC<{
  stats: HomeKpisData['ai']['view_quick_stats'];
}> = ({ stats }) => {
  const { t } = useTranslation('Dashboard');
  const zeroPct = stats.total_exams
    ? Math.round((stats.zero_view_exams / stats.total_exams) * 100)
    : 0;
  const items = [
    { label: t('analytics.exams'), value: stats.total_exams.toLocaleString() },
    {
      label: t('kpis.views.avgDistinct'),
      value: stats.avg_distinct_views_per_exam.toLocaleString(),
    },
    {
      label: t('kpis.views.zeroViewExams'),
      value: `${stats.zero_view_exams.toLocaleString()} (${zeroPct}%)`,
    },
    {
      label: t('kpis.views.distinctTypes'),
      value: stats.distinct_view_types.toLocaleString(),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-xl border border-gray-100 dark:border-gray-700 bg-card-color-200 dark:bg-gray-900/40 p-4"
        >
          <div className="mb-3 inline-flex rounded-lg bg-white dark:bg-gray-800 p-2 text-lg text-cyan-600 dark:text-cyan-400">
            <FiEye />
          </div>
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {item.label}
          </div>
          <div className="mt-1 text-2xl font-bold text-title-page-color-100 dark:text-white">
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
};

export const FacilityComparisonTable: React.FC<{
  rows: HomeKpisFacilityComparisonItem[];
}> = ({ rows }) => {
  const { t } = useTranslation('Dashboard');
  if (!rows.length) {
    return (
      <NoDataPlaceholder
        className="h-48"
        message={t('kpis.facilities.noComparison')}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.facility')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('analytics.patients')}
            </th>
            <th className="py-2 pr-4 font-semibold">{t('analytics.exams')}</th>
            <th className="py-2 pr-4 font-semibold">
              {t('analytics.reports')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.stats.viewsPerExam')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.facility_id}
              className="border-b border-gray-50 dark:border-gray-800 text-gray-700 dark:text-gray-300"
            >
              <td className="py-3 pr-4 font-semibold text-title-page-color-100 dark:text-white">
                {row.facility_name}
              </td>
              <td className="py-3 pr-4">
                {row.total_patients.toLocaleString()}
              </td>
              <td className="py-3 pr-4">
                {row.total_studies.toLocaleString()}
              </td>
              <td className="py-3 pr-4">
                {row.total_reports.toLocaleString()}
              </td>
              <td className="py-3 pr-4">
                {row.views_per_exam.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const FacilityActivityTable: React.FC<{
  rows: HomeKpisFacilityComparisonItem[];
}> = ({ rows }) => {
  const { t } = useTranslation('Dashboard');
  if (!rows.length) {
    return (
      <NoDataPlaceholder
        className="h-48"
        message={t('kpis.facilities.noComparison')}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.facility')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.activeDays')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.firstDay')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.lastDay')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('kpis.facilities.peakDay')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.facility_id}
              className="border-b border-gray-50 dark:border-gray-800 text-gray-700 dark:text-gray-300"
            >
              <td className="py-3 pr-4 font-semibold text-title-page-color-100 dark:text-white">
                {row.facility_name}
              </td>
              <td className="py-3 pr-4">{row.active_days.toLocaleString()}</td>
              <td className="py-3 pr-4">{row.first_activity_date || '—'}</td>
              <td className="py-3 pr-4">{row.last_activity_date || '—'}</td>
              <td className="py-3 pr-4">
                {row.peak_day_date
                  ? `${row.peak_day_date} (${row.peak_day_exams})`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
